import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    ApplePaySession?: any;
  }
}

const MAX_APPLE_PAY_VERSION = 14;

const hasApplePaySession = (): boolean => {
  return (
    typeof window !== "undefined" &&
    !!window.ApplePaySession &&
    typeof window.ApplePaySession === "function"
  );
};

const canMakePayments = (): boolean => {
  return (
    hasApplePaySession() &&
    typeof window.ApplePaySession.canMakePayments === "function" &&
    window.ApplePaySession.canMakePayments()
  );
};

const getSupportedApplePayVersion = (): number | null => {
  if (!hasApplePaySession()) return null;
  for (let version = MAX_APPLE_PAY_VERSION; version >= 1; version--) {
    try {
      if (window.ApplePaySession.supportsVersion(version)) {
        return version;
      }
    } catch (error) {
      console.log("Error checking Apple Pay version:", error);
    }
  }
  return null;
};

export type UseApplePayLoaderResult = {
  loaded: boolean;
  error: boolean;
  latestVersion?: number | null;
  isApplePaySupported: boolean;
};

export const useApplePayLoader = (): {
  loaded: boolean;
  error: boolean;
  latestVersion?: number | null;
  isApplePaySupported: boolean;
} => {
  const [loaded, setLoaded] = useState(hasApplePaySession());
  const [error, setError] = useState(false);

  const [latestVersion, setLatestVersion] = useState<number | null>(
    getSupportedApplePayVersion(),
  );
  const [isApplePaySupported, setIsApplePaySupported] =
    useState(canMakePayments());

  const onLoadRef = useRef<(() => void) | null>(null);
  const onErrorRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Skip SDK loading if ApplePaySession isn't available
    if (!hasApplePaySession()) {
      setLoaded(true); // Mark as "loaded" (no SDK needed)
      setIsApplePaySupported(false);
      setLatestVersion(null);
      return;
    }

    const scriptId = "apple-pay-sdk";
    let script: HTMLScriptElement | null = document.getElementById(
      scriptId,
    ) as HTMLScriptElement;

    const handleLoad = () => {
      setLoaded(hasApplePaySession());
      setIsApplePaySupported(canMakePayments());
      setLatestVersion(getSupportedApplePayVersion());
    };

    const handleError = () => {
      setError(true);
    };

    onLoadRef.current = handleLoad;
    onErrorRef.current = handleError;

    if (hasApplePaySession()) {
      handleLoad();
    }

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.src =
        "https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js";
      script.crossOrigin = "anonymous";
      script.addEventListener("load", handleLoad);
      script.addEventListener("error", handleError);
      document.head.appendChild(script);
    } else {
      script.addEventListener("load", handleLoad);
      script.addEventListener("error", handleError);
    }

    return () => {
      const script = document.getElementById(
        scriptId,
      ) as HTMLScriptElement | null;
      if (script) {
        if (onLoadRef.current) {
          script.removeEventListener("load", onLoadRef.current);
        }
        if (onErrorRef.current) {
          script.removeEventListener("error", onErrorRef.current);
        }
      }
    };
  }, []);

  return { loaded, error, latestVersion, isApplePaySupported };
};
